import React, { useEffect, useState } from "react";
import { t } from "@lingui/core/macro";
import classNames from "classnames";
import { Prompt } from "react-router";
import BackToTop from "../BackToTop/BackToTop";
import BreadcrumbsUpdater, { BreadcrumbsUpdaterProps } from "./BreadcrumbsUpdater";
import PagePadding, { PagePaddingProps } from "./PagePadding";
import PageTrackingContext, { PageTrackingContextProps } from "./PageTrackingContext";
import styles from "./Page.module.scss";

export type PageProps = {
    /** Additional CSS classes for the page container. */
    className?: string;
    /** The content of the page, can be a single ReactNode or an array of ReactNodes. */
    children?: React.ReactNode | React.ReactNode[];
    /** If true, enables scroll behavior for the page. */
    scroll?: boolean;
    /** Callback function for scroll events. */
    onScroll?: (event: React.UIEvent<HTMLDivElement, UIEvent>) => void;
    /** If true, shows a warning prompt before leaving the page. */
    shouldWarnBeforeLeave?: boolean;
    /** Reference to the view container element. */
    viewContainerRef?: any;
    /** Callback function to set the reference of the page container element. */
    setRef?: (element: HTMLDivElement) => void;
} & Omit<PagePaddingProps, "children"> &
    Omit<PageTrackingContextProps, "children"> &
    BreadcrumbsUpdaterProps;

/**
 * The Page component is used to create a structured layout for a web page,
 * ensuring consistent padding, scroll behavior, breadcrumbs, warning prompts, and tracking.
 * It can be customized with various props to fit different use cases.
 */
const Page = ({
    children,
    className,
    scroll,
    onScroll,
    shouldWarnBeforeLeave,
    viewContainerRef,
    setRef,

    // PageTrackingContext props
    trackingPage,
    trackingAttributes,
    prodTour,

    // BreadcrumbsUpdater props
    showBreadcrumbs,
    breadcrumbParams,
    titleParam,
    useLocationPath,

    // PagePadding props
    ...paddingProps
}: PageProps) => {
    const [refContainer, setRefContainer] = useState<HTMLDivElement>(); // using state and not ref to rerender and update the ref in BackToTop
    // warn before leave
    useEffect(() => {
        // TODO Since we use <Prompt>, can we remove this code?
        window.removeEventListener("beforeunload", onWarnBeforeUnload);
        if (shouldWarnBeforeLeave) {
            window.addEventListener("beforeunload", onWarnBeforeUnload);
            return () => window.removeEventListener("beforeunload", onWarnBeforeUnload);
        }
    }, [shouldWarnBeforeLeave]);

    return (
        <>
            <PageTrackingContext
                trackingPage={trackingPage}
                trackingAttributes={trackingAttributes}
                prodTour={prodTour}>
                <PagePadding
                    ref={node => {
                        setRefContainer(node);
                        setRef?.(node);
                    }}
                    markScrollRoot={scroll}
                    onScroll={onScroll}
                    {...paddingProps}
                    className={classNames(className, !scroll && styles.noScroll)}>
                    {children}
                    {scroll && <BackToTop scrollableElement={viewContainerRef ?? refContainer} />}
                </PagePadding>
            </PageTrackingContext>

            <BreadcrumbsUpdater
                showBreadcrumbs={showBreadcrumbs}
                breadcrumbParams={breadcrumbParams}
                titleParam={titleParam}
                useLocationPath={useLocationPath}
            />

            <Prompt
                when={!!shouldWarnBeforeLeave}
                message={t`Are you sure you want to leave? Changes you made may not be saved.`}
            />
        </>
    );
};

function onWarnBeforeUnload(e: BeforeUnloadEvent) {
    e.preventDefault();
    e.returnValue = true;
}

export default Page;
